import { TwoFAType } from '../../domain/two-factor';
import { TwoFactorMethodSetupCommonStep } from '../two-factor-method-setup-common/two-factor-method-setup-common.component';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  ViewChild
} from '@angular/core';
import { Validators } from '@angular/forms';
import { I18nService } from '@ppl/i18n';
import { PPL_DIALOG_DATA, PplDialogRef } from '@ppl/ui/dialog';
import { TFormBuilder } from '@ppl/ui/form';
import { createDialogCloseResponse, createDialogSuccessResponse } from '@ppl/utils/dialog.utils';
import { MemoizeLast } from '@ppl/utils/memoize.utils';
import QRCode from 'qrcode';
import type { TFormGroup } from '@ppl/ui/form';
import type { DialogResponse } from '@ppl/utils/dialog.utils';
import type { TwoFactorMethodSetupCommonFinishData, TwoFactorMethodSetupCommonInitData } from '../two-factor-method-setup-common/two-factor-method-setup-common.component';


@Component({
    selector: 'ppl-two-factor-method-setup-auth-app',
    templateUrl: './two-factor-method-setup-auth-app.component.html',
    styleUrls: ['./two-factor-method-setup-auth-app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TwoFactorMethodSetupAuthAppComponent {

  @ViewChild('qrCodeContainer', { static: false }) qrCodeContainer: ElementRef<HTMLDivElement>;

  form: TFormGroup<TwoFactorMethodSetupAuthAppFormData>;

  step = TwoFactorMethodSetupCommonStep.INIT;

  authKey: string;

  TwoFAType = TwoFAType;
  TwoFactorMethodSetupCommonStep = TwoFactorMethodSetupCommonStep;

  get securityToken() {
    return this.data.securityToken;
  }

  get actionDisabled() {
    return this.step === TwoFactorMethodSetupCommonStep.INIT
      ? !this.form.valid
      : false;
  }

  @MemoizeLast<TwoFactorMethodSetupAuthAppComponent>(['authKey'])
  get authKeyFormatted() {
    const numInPair = 4;
    const numOfPairs = this.authKey.length / numInPair;
    const stringPairs: string[] = [];

    for (let i = 0, j = 0; j < numOfPairs; i += numInPair, j++) {
      stringPairs.push(this.authKey.slice(i, i + numInPair));
    }

    return stringPairs.join(' ');
  }

  constructor(
    @Inject(PPL_DIALOG_DATA) private data: TwoFactorMethodSetupAuthAppData,
    private dialogRef: PplDialogRef<
      TwoFactorMethodSetupAuthAppComponent,
      DialogResponse<TwoFactorMethodSetupAuthAppResponse>
    >,
    private fb: TFormBuilder,
    private i18nService: I18nService
  ) {
    this.form = this.fb.group<TwoFactorMethodSetupAuthAppFormData>({
      name: ['', Validators.required]
    });

    this.getSetupData = this.getSetupData.bind(this);
  }

  onCancel() {
    this.dialogRef.close(createDialogCloseResponse());
  }

  onFinish(data: TwoFactorMethodSetupCommonFinishData) {
    this.dialogRef.close(createDialogSuccessResponse(data));
  }

  getSetupData() {
    const { name } = this.form.value;
    return { name };
  }

  onNextStep(data: TwoFactorMethodSetupCommonInitData) {
    switch (this.step) {
      case TwoFactorMethodSetupCommonStep.INIT:
        this.authKey = data.key;
        this.generateQRCode({
          key: data.key,
          issuer: 'Pipeliner',
          email: this.data.email
        });
        this.step = TwoFactorMethodSetupCommonStep.CUSTOM;
        break;
      case TwoFactorMethodSetupCommonStep.CUSTOM:
        this.step = TwoFactorMethodSetupCommonStep.VERIFY;
        break;
    }
  }

  private generateQRCode(data: { email: string, key: string, issuer: string; }) {
    const issuer = encodeURIComponent(data.issuer);
    const url = `otpauth://totp/${issuer}:${encodeURIComponent(data.email)}?secret=${data.key}&issuer=${issuer}`;
    QRCode.toCanvas(url, { width: 148, margin: 2 }, (error, canvas) => {
      if (error) {
        setTimeout(() => {
          console.error('Generating QR Code failed.', error);
          this.qrCodeContainer.nativeElement.innerHTML = this.i18nService.translate(
            'Generating_QR_Code_failed.'
          );
        });
      } else {
        setTimeout(() => {
          this.qrCodeContainer.nativeElement.innerHTML = '';
          this.qrCodeContainer.nativeElement.appendChild(canvas);
        });
      }
    });
  }
}


export interface TwoFactorMethodSetupAuthAppResponse {
  methodId: number;
}

export interface TwoFactorMethodSetupAuthAppData {
  email: string;
  securityToken: string;
}

interface TwoFactorMethodSetupAuthAppFormData {
  name: string;
}
